import { Download } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, LinearProgress, Pagination, Paper, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { USER_NAME_ROLE } from "src/app/auth";
import { deleteAllSoftwareLogs, getSoftwareLogs } from "src/app/services";
import { SoftwareLog } from "src/app/types";
import { downloadFile } from "src/app/utils/functions";
import { convertUTCDateToLocalTime, formatDateSafe, formatToUTC } from "src/app/utils/helper";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e8e8ed",
        color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const SoftwareLogsPageContent = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [logs, setLogs] = useState<SoftwareLog[]>([]);
    const [page, setPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);

    useEffect(() => {
        setLoading(true);
        getSoftwareLogs(page)
            .then((response) => {                
                setLogs(response.data.data);
                setTotalLogs(response.data.total);
            })
            .catch((err) => {
                enqueueSnackbar({
                    message: "Error fetching software logs.",
                    variant: "error",
                });
            })
            .finally(() => setLoading(false));
    }, [page]);

    const getActionName = useCallback((action: number) => {
        switch (action) {
            case 0:
                return "Created";
            case 1:
                return "Deleted";
            case 2:
                return "Updated";
            case 3:
                return "Deactivated";
            case 4:
                return "Activated";
            default:
                return "Unknown";
        }
    }, []);
    const truncateWithDots = (text: string, limit: number): string => {
        if (text.length <= limit) return text;
        return text.substring(0, limit) + "...";
    };
    const handleClearLogsClick = () => {
        deleteAllSoftwareLogs().then(() => {
            enqueueSnackbar({
                message: "All software logs cleared successfully.",
                variant: "success",
            });
            setLogs([]);
        }).catch((err) => {
            console.error("Error clearing software logs: ", err);
            enqueueSnackbar({
                message: "Error clearing software logs.",
                variant: "error",
            });
        });
    }
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ my: loading ? 1 : 2 }}>{loading && <LinearProgress />}</Box>
            {!loading && sessionStorage.getItem(USER_NAME_ROLE) === "admin" && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 2,
                    }}
                >
                    <Button
                        size="medium"
                        sx={{ mr: "18px" }}
                        variant="outlined"
                        onClick={handleClearLogsClick}
                    >
                        Clear Logs
                    </Button>
                </Box>
            )}
            <Grid container>
                <Grid item xs={12} lg={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 500 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="center">Logged At</StyledTableCell>
                                    <StyledTableCell align="center">User</StyledTableCell>
                                    <StyledTableCell align="center">
                                        UI Version
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        Software Version
                                    </StyledTableCell>
                                    <StyledTableCell align="center">Download</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 && (
                                    <StyledTableRow>
                                        <StyledTableCell align="center" colSpan={7}>
                                            {loading ? "Loading..." : "No logs."}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                                {logs.map((row, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {formatToUTC(row.created_at)} {!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.ui_version
                                                ? row.ui_version
                                                : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row" >
                                            <Tooltip title={row.software_version || "-"} arrow>
                                                <Box sx={{ cursor: "pointer" }}>
                                                    {row.software_version
                                                        ? truncateWithDots(row.software_version, 80)
                                                        : "-"}
                                                </Box>
                                            </Tooltip>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <IconButton
                                                onClick={() => {
                                                    const content = `
                                                    Logged At: ${formatToUTC(row.created_at)} ${!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                                    User: ${row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                                    UI Version: ${row.ui_version ? row.ui_version : "-"}
                                                    Software Version: ${row.software_version ? row.software_version : "-"}
                                                    `.replace(/^\s+/gm, "").trim();

                                                    downloadFile(
                                                        `software-log-${row.created_at}.txt`,
                                                        content,
                                                        "text/plain"
                                                    );
                                                }}
                                                aria-label="Export txt"
                                                color="primary"
                                                title="Download File"
                                            >
                                                <Download />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                        }}
                    >
                        <Pagination
                            count={Math.ceil(totalLogs / 10)}
                            shape="rounded"
                            size="large"
                            page={page}
                            onChange={(e, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SoftwareLogsPageContent;